import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { phone } from 'phone';

import { useAppWidthClass } from '../../utils/hooks';
import {
  citySelector,
  countrySelector,
  customerNameSelector,
  phoneOrMobileSelector,
  postCodeSelector,
  streetSelector
} from '../../selectors';

import { Module } from '../../components/common';
import {
  ReturnGoodsDetailsModal,
  ReturnGoodsEditView
} from '../../components/return-goods';
import ReturnGoodsSummaryView from '../../components/return-goods/ReturnGoodsSummaryView';
import { parsePhoneFromString } from '../../utils/phone';

const useStyles = makeStyles(theme => ({
  module: {
    margin: `${theme.spacing(5)}px ${theme.spacing(2)}px`,
    padding: theme.spacing(5)
  },
  moduleTablet: {
    margin: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
    padding: theme.spacing(2)
  },
  moduleMobile: {
    margin: theme.spacing(2),
    padding: theme.spacing(2)
  },
  content: {
    maxWidth: 410
  },
  contentTabletMobile: {
    width: '100%'
  }
}));

const ReturnGoodsPage = () => {
  const classes = useStyles();
  const { isDesktopWidth, isTabletWidth, isMobileWidth } = useAppWidthClass();

  const customerNameSaved = useSelector(customerNameSelector);
  const streetSaved = useSelector(streetSelector);
  const postCodeSaved = useSelector(postCodeSelector);
  const citySaved = useSelector(citySelector);
  const addressCountrySaved = useSelector(countrySelector);
  const savedPhone = useSelector(phoneOrMobileSelector);

  const [customerName, setCustomerName] = useState(customerNameSaved);
  const [street, setStreet] = useState(streetSaved);
  const [postCode, setPostCode] = useState(postCodeSaved);
  const [city, setCity] = useState(citySaved);
  const [addressCountry, setAddressCountry] = useState(addressCountrySaved);
  const [phoneNr, setPhoneNr] = useState(parsePhoneFromString(savedPhone));
  const [isModalOpen, setModalOpen] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [pickupDate, setPickupDate] = useState(null);
  const [time, setTime] = useState(null);

  useEffect(() => {
    setCustomerName(customerNameSaved);
  }, [customerNameSaved]);

  useEffect(() => {
    setStreet(streetSaved);
  }, [streetSaved]);

  useEffect(() => {
    setPostCode(postCodeSaved);
  }, [postCodeSaved]);

  useEffect(() => {
    setCity(citySaved);
  }, [citySaved]);

  useEffect(() => {
    setAddressCountry(addressCountrySaved);
  }, [addressCountrySaved]);

  useEffect(() => {
    setPhoneNr(parsePhoneFromString(savedPhone));
  }, [savedPhone]);

  const onDeclineButton = () => {
    setModalOpen(true);
  };

  const onCloseModal = () => {
    setModalOpen(false);
  };

  const onModalFormSubmit = values => {
    onCloseModal();
    setCustomerName(values.customerName);
    setStreet(values.street);
    setPostCode(values.postCode);
    setCity(values.city);
    setAddressCountry(values.country);
    setPhoneNr(parsePhoneFromString(values.phone.value));
  };

  const handleConfirm = (pickupDate, time) => {
    setPickupDate(pickupDate);
    setTime(time);
    setShowSummary(true);
  };

  return (
    <Module
      id="return-goods-page-content"
      className={classNames(
        classes.module,
        isTabletWidth && classes.moduleTablet,
        isMobileWidth && classes.moduleMobile
      )}
    >
      {!showSummary && (
        <ReturnGoodsEditView
          contentClass={
            isDesktopWidth ? classes.content : classes.contentTabletMobile
          }
          onDeclineButton={onDeclineButton}
          onConfirm={handleConfirm}
          customerName={customerName}
          street={street}
          postCode={postCode}
          city={city}
          addressCountry={addressCountry}
          phone={phoneNr.value}
        />
      )}
      {showSummary && (
        <ReturnGoodsSummaryView
          contentClass={
            isDesktopWidth ? classes.content : classes.contentTabletMobile
          }
          pickupDate={pickupDate}
          time={time}
          customerName={customerName}
          street={street}
          postCode={postCode}
          city={city}
          addressCountry={addressCountry}
          phone={phoneNr.value}
        />
      )}
      <ReturnGoodsDetailsModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        onSubmit={onModalFormSubmit}
        initialValues={{
          customerName,
          street,
          postCode,
          city,
          country: addressCountry,
          phone: phoneNr
        }}
      />
    </Module>
  );
};

export default ReturnGoodsPage;
